/**
 * Custom $fetch with global 401 -> force logout.
 *
 * Overrides Nuxt's auto-imported $fetch via nuxt.config.ts imports config.
 * All code that uses `$fetch(...)` without explicit import will use this version.
 *
 * When a request to our API fails with 401, the user's session is considered
 * dead: we fire-and-forget the full logout flow (clears stores, localStorage,
 * Privy, reopens the login modal) and re-throw the original error so the
 * caller's UI (BetPanel, etc.) still sees the failure and can display its own
 * error state. We do NOT retry the request.
 */

import { $fetch as originalFetch } from 'ofetch'
import * as Sentry from '@sentry/nuxt'
import { handle401Logout } from './handle401Logout'

/**
 * Read the active locale from the `i18n_locale` cookie without needing Nuxt
 * context (this wrapper runs in microtasks / outside setup). Falls back to 'en'.
 */
function getLocaleHeader(): string {
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|;\s*)i18n_locale=([^;]+)/)
    if (match) return decodeURIComponent(match[1])
  }
  return 'en'
}

function clientCountryHeader(): Record<string, string> {
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|;\s*)client_country=([^;]+)/)
    if (match) return { 'X-Client-Country': decodeURIComponent(match[1] as string) }
  }
  return {}
}

/**
 * Module-level dedup flag — when multiple requests fail with 401 in parallel
 * (e.g. on page load with stale session), only the first triggers the logout
 * flow. Subsequent 401s re-throw immediately so the caller's UI still sees
 * the failure but we don't fire N parallel /auth-logout calls.
 */
let logoutInProgress = false

/**
 * Endpoints whose 401 must NOT force a global logout. cosmetics-inventory is
 * fetched fire-and-forget and can legitimately 401 during the OAuth callback
 * window (Supabase session already exists, backend session not yet created) —
 * treating that as fatal would sign the user out of the session Google just
 * created and abort the login handshake.
 */
const NON_FATAL_401_ENDPOINTS = ['/cosmetics-inventory']

/**
 * Throttle window for API-failure reports, keyed per (flow, endpoint, status).
 * When the backend goes down every in-flight call fails at once, across every
 * open tab — without this, one outage would spend the whole monthly quota and
 * bury the very errors it produced.
 */
const API_FAILURE_THROTTLE_MS = 60_000
const lastApiFailureAt = new Map<string, number>()

/**
 * Ceiling on distinct throttle keys. The map is module state — on the Nitro
 * server it lives for the whole worker and is shared across every visitor — so
 * it needs a hard bound even though `normalizeEndpoint` already collapses the
 * dynamic segments that would otherwise make keys unbounded.
 */
const MAX_THROTTLE_KEYS = 200

/** Looks like an id rather than a route name: a hash/uuid, or a long number. */
const DYNAMIC_SEGMENT = /^(0x)?[0-9a-f]{8,}$|^[0-9a-f]{8}-[0-9a-f-]+$|^\d{3,}$/i

/**
 * Path of a request relative to our API base, with id-shaped segments masked.
 *
 * Keying off the LAST path segment would break in three ways at once, all on
 * the endpoints that matter most: `/round-bets/12345` would mint a brand-new
 * throttle key every round (so an outage reports unthrottled and the map grows
 * forever), `/user-profile-public/{uuid}` would put a user id into an indexed
 * Sentry tag, and `/deposits/limits` would come out as `limits` — losing the
 * deposit flow on the exact call the deposit modal opens with.
 */
function normalizeEndpoint(request: string, apiBase: string): string {
  const path = request.split('?')[0] ?? ''
  const relative = path.startsWith(apiBase) ? path.slice(apiBase.length) : path
  const masked = relative
    .replace(/^\/+/, '')
    .split('/')
    .map(segment => (DYNAMIC_SEGMENT.test(segment) ? ':id' : segment))
    .join('/')
  return masked || 'unknown'
}

/**
 * Which product flow an endpoint belongs to, derived from its path.
 *
 * This is what makes a `flow:deposit` alert rule possible without hand-tagging
 * every call site: one wrapper covers every endpoint the app talks to.
 */
function flowForEndpoint(endpoint: string): string {
  if (endpoint.startsWith('deposit')) return 'deposit'
  if (endpoint.startsWith('withdrawal')) return 'withdrawal'
  if (endpoint.startsWith('auth-') || endpoint.startsWith('verify-')) return 'auth'
  if (endpoint.startsWith('crash-') || endpoint.startsWith('game-')) return 'game'
  if (endpoint.startsWith('coupon-') || endpoint.startsWith('bonus-')) return 'bonus'
  return 'api'
}

/** Drop entries past the throttle window, then oldest-first if still over. */
function evictThrottleKeys(now: number): void {
  for (const [key, at] of lastApiFailureAt) {
    if (now - at >= API_FAILURE_THROTTLE_MS) lastApiFailureAt.delete(key)
  }
  while (lastApiFailureAt.size >= MAX_THROTTLE_KEYS) {
    const oldest = lastApiFailureAt.keys().next().value
    if (oldest === undefined) break
    lastApiFailureAt.delete(oldest)
  }
}

/**
 * Report a failed call to our own API.
 *
 * Deliberately ignores 4xx: those are this request being rejected on its own
 * merits — validation, limits, auth — and the caller renders them. What we
 * capture is the platform failing underneath the user: a 5xx, or — from a
 * visible, online device — no status at all, which means the request never
 * completed (network down, DNS, CORS, blocked by an extension) and is otherwise
 * completely invisible to us.
 */
function reportApiFailure(request: string, error: unknown, apiBase: string): void {
  const statusCode = (error as { statusCode?: number }).statusCode
  if (typeof statusCode === 'number' && statusCode < 500) return

  // A cancelled request is the app working correctly — the user navigated away,
  // the component unmounted, or a timeout elapsed. It carries no status, so it
  // would otherwise be reported as a network failure and burn this endpoint's
  // throttle slot on a non-event, hiding a real 5xx in the following minute.
  //
  // ofetch never rethrows the raw abort: it wraps every failure in a FetchError
  // whose own `name` is always "FetchError", and puts the original on `cause`.
  // Checking only `error.name` here would therefore match nothing in production.
  const abortName = (error as { name?: string })?.name
  const causeName = (error as { cause?: { name?: string } })?.cause?.name
  if (
    abortName === 'AbortError' || abortName === 'TimeoutError'
    || causeName === 'AbortError' || causeName === 'TimeoutError'
  ) return

  const endpoint = normalizeEndpoint(request, apiBase)
  const flow = flowForEndpoint(endpoint)

  // Network failures only — a 5xx means a server answered and is always ours to
  // explain. Without a status the request never reached anyone, and the cause is
  // usually on this device rather than in our API:
  //
  //   - a hidden page: the browser cancels in-flight requests when a tab is
  //     backgrounded or closed. iOS Safari reports that as `Load failed`, with
  //     no AbortError anywhere, so the check above cannot catch it.
  //   - a device that knows it is offline.
  //
  // Both are indistinguishable from a real outage in the payload, which is why
  // these were the single largest source of new events after the last rollout:
  // mobile ad traffic on interstitials, where users leave mid-load.
  //
  // Deposit and auth are exempt from the visibility half. Backgrounding the page
  // is the NORMAL shape of those flows — paying a Lightning invoice in a wallet
  // app, waiting on an emailed code — and their pollers keep running while
  // hidden. Dropping their failures would blind precisely the flow this
  // reporting was built for.
  //
  // Scope note: the exemption follows flowForEndpoint, so `auth` here means the
  // `auth-*` and `verify-*` prefixes. The wallet deep-link round trip
  // (`user-link-wallet*`, `user-link-method`) maps to `api` and is NOT exempt,
  // even though it backgrounds the page too — widen this if that flow ever needs
  // the same treatment.
  //
  // Offline still drops everything, deposit included: nothing our API does can
  // explain a request made with no connection.
  if (statusCode === undefined) {
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false
    const visibilityExempt = flow === 'deposit' || flow === 'auth'

    if (offline || (hidden && !visibilityExempt)) {
      // Breadcrumb rather than a silent return: this keeps the drop auditable —
      // both to tell "the filter works" from "the filter ate an outage", and to
      // show on the next captured event that N of these preceded it.
      //
      // Throttled on the same map, because unthrottled it would defeat itself:
      // Sentry keeps the last 100 breadcrumbs, and an offline user sitting on the
      // Lightning screen polls every 3s. Within five minutes the buffer would hold
      // nothing but identical drop notices — evicting the auth, navigation and
      // websocket context that makes the next real event readable.
      const dropKey = `drop:${flow}:${endpoint}`
      const dropAt = Date.now()
      const lastDrop = lastApiFailureAt.get(dropKey)
      if (lastDrop === undefined || dropAt - lastDrop >= API_FAILURE_THROTTLE_MS) {
        if (lastApiFailureAt.size >= MAX_THROTTLE_KEYS) evictThrottleKeys(dropAt)
        lastApiFailureAt.set(dropKey, dropAt)
        Sentry.addBreadcrumb({
          category: 'fetch',
          level: 'warning',
          message: `dropped network failure (${offline ? 'offline' : 'hidden'})`,
          data: { endpoint, flow },
        })
      }
      return
    }
  }

  const status = statusCode ?? 'network'

  const key = `${flow}:${endpoint}:${status}`
  const now = Date.now()
  const last = lastApiFailureAt.get(key)
  if (last !== undefined && now - last < API_FAILURE_THROTTLE_MS) return

  if (lastApiFailureAt.size >= MAX_THROTTLE_KEYS) evictThrottleKeys(now)
  lastApiFailureAt.set(key, now)

  Sentry.captureException(error, {
    tags: { flow, endpoint, status: String(status) },
    extra: {
      // The masked path, not the raw URL — so the indexed tag and this context
      // stay free of round/user ids.
      //
      // Note this does NOT make the event as a whole id-free: ofetch builds its
      // FetchError message as `[GET] "<full url>": <status>`, and Sentry ships
      // that as the issue title. Scrubbing it would cost the stack trace, and
      // the user id is already attached deliberately via Sentry.setUser, so the
      // exposure here is the same identifier by another route — not new.
      endpoint,
      message: (error as { message?: string }).message,
      data: (error as { data?: unknown }).data,
    },
  })
}

/**
 * Drop-in replacement for ofetch's $fetch.
 * Intercepts ANY 401 from our API and triggers the global logout flow.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const $fetch = function sessionRefreshFetch(request: any, opts?: any) {
  // Determine if this is our API call
  let apiBase: string | undefined
  try {
    apiBase = useRuntimeConfig().public.apiBase as string
  } catch {
    // useRuntimeConfig not available (SSR or outside Nuxt context)
    return originalFetch(request, opts)
  }

  const isOurApi = typeof request === 'string' && apiBase && request.startsWith(apiBase)
  const isAuthCall = typeof request === 'string' &&
    (request.includes('/auth-login') || request.includes('/auth-logout'))

  // Attach the active locale to every call to OUR API (auth calls too, so
  // signup captures the user's language). Caller-supplied headers win.
  const reqOpts = isOurApi
    ? { ...opts, headers: { 'X-Lang': getLocaleHeader(), ...clientCountryHeader(), ...(opts?.headers ?? {}) } }
    : opts

  // Pass through: non-API calls
  if (!isOurApi) {
    return originalFetch(request, reqOpts)
  }

  // Wrap in failure reporting + 401 -> logout logic
  return originalFetch(request, reqOpts).catch((error: unknown) => {
    reportApiFailure(request as string, error, apiBase as string)

    const statusCode = (error as { statusCode?: number }).statusCode
    // Auth routes report their failures like everything else, but must not run
    // the logout flow below — logging out in response to /auth-logout failing
    // is circular, and /auth-login's own 401 is just bad credentials.
    if (isAuthCall || statusCode !== 401) throw error

    // Non-fatal endpoints: surface the 401 to the caller without logging out.
    if (typeof request === 'string' && NON_FATAL_401_ENDPOINTS.some((endpoint) => request.includes(endpoint))) {
      throw error
    }

    // DEDUP: if a logout is already in flight from a previous concurrent 401,
    // skip everything and just re-throw so the caller's UI sees the failure.
    if (logoutInProgress) {
      throw error
    }
    logoutInProgress = true

    const errorData = (error as { data?: { reason?: string; message?: string } }).data
    const reason = errorData?.reason ?? 'unknown'
    const endpoint = typeof request === 'string' ? request.split('/').pop() : 'unknown'

    Sentry.addBreadcrumb({
      category: 'auth',
      message: '401 received, forcing logout',
      level: 'warning',
      data: { endpoint, reason },
    })
    console.warn('[auth] Got 401, forcing logout. reason=', reason)

    // STEP 1 — ALWAYS run synchronous client-side cleanup first.
    // Never throws, idempotent, does not depend on Nuxt context. This
    // guarantees the user is visibly logged out (modal open, stores cleared)
    // even if useAuth() below is unavailable.
    handle401Logout()

    // STEP 2 — Best-effort backend cleanup via the canonical logout flow.
    // May fail if useAuth() can't access the Nuxt context — that's OK,
    // step 1 already did the client-visible work and step 3 below
    // guarantees the URL changes even when useAuth() is unavailable.
    try {
      const { logout } = useAuth()
      logout()
        .catch((err) => {
          console.warn('[auth] backend logout failed (non-fatal):', err)
        })
        .finally(() => {
          logoutInProgress = false
        })
    } catch (err) {
      console.warn('[auth] useAuth() unavailable for backend logout (non-fatal):', err)
      logoutInProgress = false

      // STEP 3 — Hard redirect fallback. When useAuth() is unavailable
      // (typically a microtask running after the component that issued
      // the request was already unmounted), the canonical logout flow
      // never runs `router.push('/')`. Without this, the user would
      // stay on the protected URL with the modal open. A full reload
      // via `window.location.href` is brutal but correct: it tears down
      // every in-flight component, clears all module state, and lands
      // the user on the home page where the (now-cleared) auth state
      // forces a re-login.
      //
      // Skipped when we are ALREADY on '/': there is no protected URL to
      // escape from — step 1 has cleared state and opened the login modal.
      // Reloading '/' re-runs the same bootstrap that produced this 401; if
      // its trigger survives the reload, the page loops forever (prod
      // incident: /auth-session 401 with a surviving sb-* token → login
      // modal → reload → modal → reload, infinitely, on iOS Chrome).
      if (typeof window !== 'undefined' && window.location.pathname !== '/') {
        window.location.href = '/'
      }
    }

    // Re-throw the original error so the caller's UI still sees the failure.
    throw error
  })
} as typeof originalFetch

// Copy static methods from original $fetch (.create, .raw, .native)
Object.assign($fetch, originalFetch)
